import {
  loadCKEditorCloud,
  type CKEditorCloudResult,
  type CKEditorCloudConfig,
} from "@ckeditor/ckeditor5-react";
import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type ReactNode,
} from "react";

type CloudState = {
  status: "idle" | "loading" | "success" | "error"
  result: CKEditorCloudResult<CKEditorCloudConfig> | null
  error: unknown
};

type PremiumCloudState = {
  status: "idle" | "loading" | "success" | "error"
  result: CKEditorCloudResult<CKEditorCloudConfig & { premium: true }> | null
  error: unknown
};

type CKEditorCloudContextValue = {
  base: CloudState
  premium: PremiumCloudState
  requestBase: () => void
  requestPremium: () => void
};

type CKEditorCloudHookResult =
  | { status: "loading" }
  | { status: "error", error: unknown }
  | ({ status: "success" } & CKEditorCloudResult<CKEditorCloudConfig>);

const CKEditorCloudContext = createContext<CKEditorCloudContextValue | null>(null);

const CKEDITOR_VERSION = "49.0.0";

export const CKEditorCloudProvider = ({ children }: { children: ReactNode }) => {
  const [base, setBase] = useState<CloudState>({
    status: "idle",
    result: null,
    error: null,
  });

  const [premium, setPremium] = useState<PremiumCloudState>({
    status: "idle",
    result: null,
    error: null,
  });

  const baseRequested = useRef(false);
  const premiumRequested = useRef(false);

  const requestBase = useCallback(() => {
    if (baseRequested.current) return;
    baseRequested.current = true;

    setBase((prev) => ({ ...prev, status: "loading" }));

    loadCKEditorCloud({ version: CKEDITOR_VERSION, translations: ["fr"], premium: false })
      .then((result) => {
        setBase({ status: "success", result: result, error: null });
      })
      .catch(() => {
        baseRequested.current = false;
        setBase({ status: "error", result: null, error: new Error("Failed to load CKEditor cloud") });
      });
  }, []);

  const requestPremium = useCallback(() => {
    if (premiumRequested.current) return;
    premiumRequested.current = true;

    setPremium((prev) => ({ ...prev, status: "loading" }));

    loadCKEditorCloud({ version: CKEDITOR_VERSION, translations: ["fr"], premium: true })
      .then((result) => {
        setPremium({ status: "success", result: result, error: null });
      })
      .catch(() => {
        premiumRequested.current = false;
        setPremium({ status: "error", result: null, error: new Error("Failed to load CKEditor premium cloud") });
      });
  }, []);

  const contextValue = useMemo(
    () => ({
      base, premium, requestBase, requestPremium,
    }),
    [base, premium, requestBase, requestPremium],
  );

  return (
    <CKEditorCloudContext.Provider value={contextValue}>
      {children}
    </CKEditorCloudContext.Provider>
  );
};

/**
 * Returns the preloaded CKEditor cloud result.
 * Triggers base bundle loading on first call.
 * When `needsPremium` is true, also triggers premium bundle loading
 * and returns the premium result once available.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useCKEditorCloudPreloaded = (needsPremium: boolean): CKEditorCloudHookResult => {
  const context = useContext(CKEditorCloudContext);

  if (!context) {
    throw new Error("useCKEditorCloudPreloaded must be used within CKEditorCloudProvider");
  }

  const {
    base, premium, requestBase, requestPremium,
  } = context;

  // Request base loading on first mount, plus premium if needed
  useEffect(() => {
    requestBase();
    if (needsPremium) {
      requestPremium();
    }
  }, [needsPremium]);

  return useMemo((): CKEditorCloudHookResult => {
    if (needsPremium) {
      if (premium.status === "success" && premium.result) {
        return { status: "success", ...premium.result };
      }
      if (premium.status === "error") {
        return { status: "error", error: premium.error };
      }
      return { status: "loading" };
    }

    if (base.status === "success" && base.result) {
      return { status: "success", ...base.result };
    }
    if (base.status === "error") {
      return { status: "error", error: base.error };
    }
    return { status: "loading" };
  }, [needsPremium, base, premium]);
};
